/**
 * src/pages/Pulse.tsx
 *
 * Category Pulse — Chase Brief #001. Per-modelKey table for the market
 * intelligence analyst: which products are hot / a steal at launch.
 *
 * The table is split into two colour-coded blocks that are NEVER blended
 * (dictionary §1):
 *   SOLD   (blue)  — what buyers actually paid   (eBay sold, windowed)
 *   ASKING (amber) — what sellers want right now  (live listings, snapshot)
 * The "steal" spread = (sold median − lowest live) / sold median.
 *
 * Field definitions: backend/product/docs/CHASE_DATA_DICTIONARY.md.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { getCategoryPulse, pulseExportPath, type PulseRow, type PulseSort } from '@/api/chase';
import TimeWindowToggle from '@/components/TimeWindowToggle';
import CsvExportButton from '@/components/CsvExportButton';
import PulseDetail from '@/components/PulseDetail';
import { cn } from '@/lib/cn';
import { txt } from '@/lib/format';

const DAY = 24 * 60 * 60 * 1000;
const CATEGORIES = ['phones', 'laptops', 'consoles', 'audio', 'monitors', 'gpus'];
const SORTS: { value: PulseSort; label: string }[] = [
  { value: 'velocity', label: 'Sold velocity (30d)' },
  { value: 'spread',   label: 'Biggest steal (spread %)' },
  { value: 'listings', label: 'Most live listings' },
  { value: 'modelKey', label: 'modelKey (A–Z)' },
];

const gbp = (n: number | null) => (n == null ? '—' : `£${n.toLocaleString()}`);
const num = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString());

function ConditionBadge({ t }: { t: 'A' | 'B' | 'C' | null }) {
  // A corrupted `condition: {}` is truthy, so `!t` let it through to be
  // rendered as a React child. Check the type, not the truthiness.
  if (typeof t !== 'string' || !t) return <span className="text-slate-400">—</span>;
  const cls = t === 'A' ? 'bg-emerald-500/15 text-emerald-600'
    : t === 'B' ? 'bg-sky-500/15 text-sky-600'
    : 'bg-amber-500/15 text-amber-600';
  return <span className={cn('px-1.5 py-0.5 rounded text-xs font-semibold', cls)}>{t}</span>;
}

function SpreadCell({ pct }: { pct: number | null }) {
  if (pct == null) return <span className="text-slate-400">—</span>;
  const strong = pct >= 20;
  return (
    <span className={cn('font-semibold tabular-nums',
      pct <= 0 ? 'text-slate-400' : strong ? 'text-emerald-600' : 'text-emerald-500')}>
      {pct > 0 ? '+' : ''}{pct}%{strong ? ' 🔥' : ''}
    </span>
  );
}

export default function Pulse() {
  const [sinceMs, setSinceMs]   = useState<number>(30 * DAY);
  const [category, setCategory] = useState<string>('phones');
  const [sort, setSort]         = useState<PulseSort>('velocity');
  const [modelKey, setModelKey] = useState<string>('');
  const [detailKey, setDetailKey] = useState<string | null>(null);

  const opts = { category: category || undefined, modelKey: modelKey || undefined, sinceMs, sort, limit: 200 };
  const { data, isLoading, isError } = useQuery({
    queryKey: ['chase-pulse', category, modelKey, sinceMs, sort],
    queryFn: () => getCategoryPulse(opts),
    staleTime: 30_000,
  });
  const rows: PulseRow[] = data?.rows ?? [];

  return (
    <div className="space-y-4 max-w-[100rem]">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold">Category Pulse</h1>
          <p className="text-sm text-slate-500 mt-1">
            Sold vs live price &amp; velocity by modelKey · <span className="text-sky-600">SOLD</span> = what buyers paid,{' '}
            <span className="text-amber-600">ASKING</span> = what sellers want (never blended)
          </p>
        </div>
        <TimeWindowToggle value={sinceMs} onChange={setSinceMs} label="window" />
      </header>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <select value={category} onChange={e => setCategory(e.target.value)}
          className="border border-slate-300 dark:border-slate-700 bg-transparent rounded px-2 py-1">
          <option value="">All categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={sort} onChange={e => setSort(e.target.value as PulseSort)}
          className="border border-slate-300 dark:border-slate-700 bg-transparent rounded px-2 py-1">
          {SORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <input value={modelKey} onChange={e => setModelKey(e.target.value)} placeholder="filter modelKey…"
          className="border border-slate-300 dark:border-slate-700 bg-transparent rounded px-2 py-1 w-48" />
        <div className="ml-auto flex items-center gap-2">
          {data && <span className="text-xs text-slate-400">{rows.length} modelKeys</span>}
          <CsvExportButton path={pulseExportPath(opts)} filename={`category_pulse_${category || 'all'}.csv`} label="CSV" />
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-x-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="animate-spin mr-2" size={18} /> Loading…</div>
        ) : isError ? (
          <div className="py-12 text-center text-rose-500 text-sm">Failed to load pulse data.</div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-slate-400 text-sm">No data for this filter.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              {/* group header */}
              <tr className="text-[11px] uppercase tracking-wide">
                <th className="text-left px-3 pt-2 pb-1" colSpan={2}></th>
                <th className="text-center px-2 pt-2 pb-1 text-sky-600 bg-sky-500/5" colSpan={6}>SOLD · eBay (what buyers paid)</th>
                <th className="text-center px-2 pt-2 pb-1 text-amber-600 bg-amber-500/5" colSpan={3}>ASKING · live (what sellers want)</th>
                <th className="px-2 pt-2 pb-1"></th>
              </tr>
              <tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-200 dark:border-slate-800">
                <th className="text-left px-3 py-1.5">modelKey</th>
                <th className="text-center px-2 py-1.5">cond</th>
                <th className="text-right px-2 py-1.5 bg-sky-500/5">median 30d</th>
                <th className="text-right px-2 py-1.5 bg-sky-500/5">low 7d</th>
                <th className="text-right px-2 py-1.5 bg-sky-500/5">avg 30d</th>
                <th className="text-right px-2 py-1.5 bg-sky-500/5">high 30d</th>
                <th className="text-right px-2 py-1.5 bg-sky-500/5">sold 7d</th>
                <th className="text-right px-2 py-1.5 bg-sky-500/5">sold 30d</th>
                <th className="text-right px-2 py-1.5 bg-amber-500/5">low (now)</th>
                <th className="text-right px-2 py-1.5 bg-amber-500/5">median</th>
                <th className="text-right px-2 py-1.5 bg-amber-500/5">live #</th>
                <th className="text-right px-3 py-1.5">steal</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.modelKey} className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                  <td className="px-3 py-1.5 font-mono text-xs">
                    <button onClick={() => setDetailKey(r.modelKey)} className="hover:underline text-sky-600 dark:text-sky-400" title="Validate: anchor breakdown + raw sold + filter flags">
                      {txt(r.modelKey)}
                    </button>
                  </td>
                  <td className="px-2 py-1.5 text-center"><ConditionBadge t={r.condition} /></td>
                  <td className="px-2 py-1.5 text-right tabular-nums bg-sky-500/5 font-medium">{gbp(r.sold_median_30d)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums bg-sky-500/5">{gbp(r.sold_low_7d)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums bg-sky-500/5">{gbp(r.sold_avg_30d)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums bg-sky-500/5">{gbp(r.sold_high_30d)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums bg-sky-500/5 text-slate-500">{num(r.sold_count_7d)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums bg-sky-500/5 text-slate-500">{num(r.sold_count_30d)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums bg-amber-500/5 font-medium">{gbp(r.ask_low)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums bg-amber-500/5">{gbp(r.ask_median)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums bg-amber-500/5 text-slate-500">{num(r.ask_count)}</td>
                  <td className="px-3 py-1.5 text-right"><SpreadCell pct={r.spread_pct} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-xs text-slate-400">
        Steal = (sold median 30d − lowest live) ÷ sold median. SOLD figures are windowed stats over real eBay sales;
        ASKING figures are a current snapshot of live listings (no history — historical asking prices need the archive layer).
        Click any modelKey to validate it (anchor breakdown + raw sold listings + filter flags).
        Definitions: <span className="font-mono">docs/CHASE_DATA_DICTIONARY.md</span>.
      </p>

      {detailKey && <PulseDetail modelKey={detailKey} onClose={() => setDetailKey(null)} />}
    </div>
  );
}
